/*!
 * BeforeAfter - a drag-to-reveal before/after image slider.
 * Vendly LLC. Rentable micro-app. Dependency-free, no build step.
 *
 * ---------------------------------------------------------------------------
 * USE
 *   <div id="ba"></div>
 *   <script>
 *     window.BeforeAfterConfig = {
 *       target: "#ba",
 *       before: "before.jpg",
 *       after:  "after.jpg",
 *       caption: "Front yard remodel",
 *       start: 50,                 // starting split %, 0-100
 *       ratio: "4/3",              // optional. default: the after image's own ratio
 *       accent: "#ffffff",         // handle line + grip colour
 *       labels: { before: "Before", after: "After" }
 *     };
 *   <\/script>
 *   <script src="beforeafter.js" defer><\/script>
 *
 *   For several sliders, add several containers and call
 *   BeforeAfter.init({...}) once per container.
 *
 *   Best results: the two photos are the same scene, same crop/angle, similar size.
 *
 * ---------------------------------------------------------------------------
 * FIX LOG 2026-08-04 - four defects found on the published RiverTown theme.
 * Jacob, looking at the live page: "the dragging before and after on the back
 * page doesn't look right." Every one of these was live and none had been
 * caught, because the automated check measured the page in a viewport that
 * was reporting zero height, wrote MOUSE_SKIP on a negative Y coordinate, and
 * the lane read the skip as a pass.
 *
 * 1. GEOMETRY. The after image sat in normal flow at its natural ratio and was
 *    never cropped. The before image was absolutely positioned at height:100%
 *    with object-fit:cover, so it WAS cropped. Two different crop rules for the
 *    two halves of one wipe. When the pair had matching ratios you could not
 *    see it. RiverTown's attic pair does not match: the before is 1400x1866
 *    (portrait) and the after is 1400x1050 (landscape), so the before half was
 *    centre-cropped to 56% of its height and blown up, and dragging revealed a
 *    zoomed image that did not line up with the other half at any position.
 *    Fix: one box, both images cropped by the identical rule, so the wipe
 *    always registers. Identical output when the ratios already match.
 *
 * 2. ROLE WITHOUT VALUE. The container carried role="slider" and never set
 *    aria-valuenow, aria-valuemin or aria-valuemax. A screen reader was told
 *    "this is a slider" and could never be told where it sat - WCAG 2.1
 *    4.1.2 Name, Role, Value. It is also why the drag test could not see
 *    movement: it read a value that did not exist and reported the default
 *    both times, so before=50 after=50 was guaranteed no matter what the
 *    drag did.
 *
 * 3. KEYBOARD DESYNC. Arrow keys tracked their own `current` variable that
 *    pointer drags never wrote to. Drag the handle to 10%, press ArrowRight,
 *    and it teleported to 54% instead of stepping to 14%. Measured, both
 *    sliders. One source of truth now.
 *
 * 4. POINTER-ONLY DEVICES DEAD. Handlers existed for mouse and for touch and
 *    for nothing else, so a pen, a stylus, or any browser that emits only
 *    Pointer Events moved nothing at all. Now a single captured pointer path
 *    handles all three, with the mouse+touch pair kept only as the fallback
 *    for browsers with no PointerEvent.
 *
 * Also removed a sourceMappingURL pointing at a .map that is not shipped, on
 * an unrelated store's CDN path. Also replaced the U+2194 arrow glyph in the
 * grip with an inline SVG: that codepoint carries the Unicode Emoji property
 * and renders as a colour emoji on some platforms, which is against the
 * house no-emoji rule.
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var DEFAULTS = {
    target: "#beforeafter",
    before: "",
    after: "",
    caption: "",
    start: 50,
    ratio: "",
    accent: "#ffffff",
    labels: { before: "Before", after: "After" }
  };

  var STEP = 4;      // arrow key step, percent
  var BIG_STEP = 10; // page up / page down step, percent

  function merge(user) {
    var c = {}, k, j;
    for (k in DEFAULTS) c[k] = DEFAULTS[k];
    for (j in user) if (user.hasOwnProperty(j)) c[j] = user[j];
    c.labels = { before: DEFAULTS.labels.before, after: DEFAULTS.labels.after };
    if (user && user.labels) {
      if (user.labels.before) c.labels.before = user.labels.before;
      if (user.labels.after) c.labels.after = user.labels.after;
    }
    return c;
  }

  function escapeAttr(s) {
    return String(s == null ? "" : s)
      .replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (ch) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch];
    });
  }

  // Allow same-origin/relative paths, http(s), protocol-relative and data:image.
  // Reject every other scheme so a config value cannot smuggle in javascript:.
  function safeSrc(raw) {
    var v = String(raw == null ? "" : raw).trim();
    if (!v) return "";
    if (/^(https?:)?\/\//i.test(v)) return v;
    if (/^[\/.#?]/.test(v)) return v;
    if (/^data:image\//i.test(v)) return v;
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return "";
    return v;
  }

  // Colours land in a style attribute, so only accept shapes that cannot carry
  // a second declaration or a url().
  function safeColor(raw) {
    var v = String(raw == null ? "" : raw).trim();
    if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
    if (/^rgba?\([\d\s.,%]+\)$/i.test(v)) return v;
    if (/^[a-z]{3,20}$/i.test(v)) return v;
    return DEFAULTS.accent;
  }

  // "4/3", "4 / 3", "1.333" and 1.333 all resolve to a CSS aspect-ratio value.
  function safeRatio(raw) {
    if (typeof raw === "number" && isFinite(raw) && raw > 0) return String(raw);
    var v = String(raw == null ? "" : raw).trim();
    if (/^\d+(\.\d+)?\s*\/\s*\d+(\.\d+)?$/.test(v)) return v.replace(/\s+/g, " ");
    if (/^\d+(\.\d+)?$/.test(v) && parseFloat(v) > 0) return v;
    return "";
  }

  function clamp(n, lo, hi) { return n < lo ? lo : (n > hi ? hi : n); }

  function injectCss() {
    if (document.getElementById("beforeafter-css")) return;
    var css = [
      ".ba-wrap{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;max-width:100%}",
      /* One box. Both images obey the same crop rule, so the wipe registers
         even when the two photos are not the same shape. */
      ".ba{position:relative;width:100%;aspect-ratio:4/3;overflow:hidden;border-radius:12px;",
      "user-select:none;-webkit-user-select:none;touch-action:none;background:#eee;line-height:0;cursor:ew-resize}",
      ".ba img{position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;display:block;",
      "pointer-events:none;-webkit-user-drag:none;user-drag:none}",
      ".ba-after{z-index:1}",
      ".ba-before{z-index:2}",
      ".ba-handle{position:absolute;top:0;bottom:0;width:2px;z-index:3;transform:translateX(-1px);box-shadow:0 0 0 1px rgba(0,0,0,.15)}",
      ".ba-grip{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:38px;height:38px;",
      "border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;",
      "z-index:4;color:#14140f}",
      ".ba-grip svg{display:block;width:20px;height:20px}",
      ".ba-tag{position:absolute;top:10px;z-index:3;background:rgba(20,20,15,.62);color:#fff;font-size:12px;",
      "font-weight:600;padding:3px 8px;border-radius:6px;line-height:1.2}",
      ".ba-tag.ba-b{left:10px}",
      ".ba-tag.ba-a{right:10px}",
      ".ba-cap{font-size:14px;color:#5c5c55;margin-top:8px;line-height:1.4}",
      /* WCAG 2.4.7. The container is the focus target, so it needs a visible ring. */
      ".ba:focus-visible{outline:3px solid #14140f;outline-offset:3px}",
      /* Browsers with no aspect-ratio fall back to the older behaviour:
         the after image sizes the box at its natural ratio. */
      "@supports not (aspect-ratio: 1 / 1){",
      ".ba{aspect-ratio:auto}",
      ".ba .ba-after{position:relative;height:auto}",
      "}"
    ].join("");
    var el = document.createElement("style");
    el.id = "beforeafter-css";
    el.textContent = css;
    document.head.appendChild(el);
  }

  var GRIP_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M9.5 7.5 5 12l4.5 4.5M14.5 7.5 19 12l-4.5 4.5"/></svg>';

  function init(userCfg) {
    var cfg = merge(userCfg || {});
    injectCss();

    var container = document.querySelector(cfg.target);
    if (!container) {
      if (window.console) console.warn("[BeforeAfter] target not found:", cfg.target);
      return;
    }
    if (!cfg.before || !cfg.after) {
      if (window.console) console.warn("[BeforeAfter] need both before and after image URLs");
      return;
    }

    var accent = safeColor(cfg.accent);
    var wrap = document.createElement("div");
    wrap.className = "ba-wrap";
    wrap.innerHTML =
      '<div class="ba">' +
        '<img class="ba-after" draggable="false" alt="' + escapeAttr(cfg.labels.after) + '" src="' + escapeAttr(safeSrc(cfg.after)) + '">' +
        '<img class="ba-before" draggable="false" alt="' + escapeAttr(cfg.labels.before) + '" src="' + escapeAttr(safeSrc(cfg.before)) + '">' +
        '<span class="ba-tag ba-b">' + escapeHtml(cfg.labels.before) + '</span>' +
        '<span class="ba-tag ba-a">' + escapeHtml(cfg.labels.after) + '</span>' +
        '<div class="ba-handle" style="background:' + escapeAttr(accent) + '">' +
          '<div class="ba-grip" style="background:' + escapeAttr(accent) + '">' + GRIP_SVG + '</div>' +
        '</div>' +
      '</div>' +
      (cfg.caption ? '<div class="ba-cap">' + escapeHtml(cfg.caption) + "</div>" : "");

    container.innerHTML = "";
    container.appendChild(wrap);

    var ba = wrap.querySelector(".ba"),
        beforeImg = wrap.querySelector(".ba-before"),
        afterImg = wrap.querySelector(".ba-after"),
        handle = wrap.querySelector(".ba-handle");

    /* Defect 1. The box gets one ratio and both images cover it. Prefer an
       explicit config ratio; otherwise take the after image's own shape once
       it has loaded, which keeps a matched pair looking exactly as before. */
    function applyRatio() {
      var r = safeRatio(cfg.ratio);
      if (!r && afterImg.naturalWidth > 0 && afterImg.naturalHeight > 0) {
        r = afterImg.naturalWidth + " / " + afterImg.naturalHeight;
      }
      if (r) ba.style.aspectRatio = r;
    }
    if (afterImg.complete) applyRatio();
    else afterImg.addEventListener("load", applyRatio);

    /* Defect 3. One variable. Pointer drags and key presses both write here,
       so the two input methods can never disagree about where the handle is. */
    var current = clamp(Number(cfg.start), 0, 100);
    if (!isFinite(current)) current = 50;

    ba.setAttribute("tabindex", "0");
    ba.setAttribute("role", "slider");
    ba.setAttribute("aria-orientation", "horizontal");
    ba.setAttribute("aria-valuemin", "0");
    ba.setAttribute("aria-valuemax", "100");
    ba.setAttribute("aria-label", cfg.caption || "Before and after image comparison");

    /* Defect 2. Every move republishes the value, so assistive tech and any
       automated check can both read where the handle actually is. */
    function setSplit(pct) {
      current = clamp(pct, 0, 100);
      var inset = "inset(0 " + (100 - current) + "% 0 0)";
      beforeImg.style.clipPath = inset;
      beforeImg.style.webkitClipPath = inset;
      handle.style.left = current + "%";
      var whole = Math.round(current);
      ba.setAttribute("aria-valuenow", String(whole));
      ba.setAttribute("aria-valuetext",
        whole + "% " + cfg.labels.before + ", " + (100 - whole) + "% " + cfg.labels.after);
    }
    setSplit(current);

    function pctFromClientX(clientX) {
      var rect = ba.getBoundingClientRect();
      if (!rect.width) return current;
      return (clientX - rect.left) / rect.width * 100;
    }

    var dragging = false;

    function onDown(e) {
      if (typeof e.button === "number" && e.button !== 0) return; // left button / primary only
      dragging = true;
      var x = e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
      if (typeof x !== "number") return;
      setSplit(pctFromClientX(x));
      if (e.cancelable) e.preventDefault();
      try { ba.focus({ preventScroll: true }); } catch (err) { /* older browsers */ }
    }

    function onMove(e) {
      if (!dragging) return;
      var x = e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
      if (typeof x !== "number") return;
      setSplit(pctFromClientX(x));
      // Belt for the touch fallback: touch-action:none already stops the scroll,
      // but a window-level touchmove must not let the page move under the thumb.
      if (e.cancelable) e.preventDefault();
    }

    function onUp() { dragging = false; }

    /* Defect 4. One captured pointer path covers mouse, touch and pen. Capture
       means a drag that leaves the image keeps tracking, and release is
       delivered even if the finger lifts off the element. mouse+touch stay only
       as the fallback for a browser with no PointerEvent. */
    if (typeof window.PointerEvent === "function") {
      ba.addEventListener("pointerdown", function (e) {
        onDown(e);
        if (dragging) { try { ba.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ } }
      });
      ba.addEventListener("pointermove", onMove);
      ba.addEventListener("pointerup", onUp);
      ba.addEventListener("pointercancel", onUp);
      window.addEventListener("blur", onUp);
    } else {
      ba.addEventListener("mousedown", onDown);
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      ba.addEventListener("touchstart", onDown, { passive: false });
      window.addEventListener("touchmove", onMove, { passive: false });
      window.addEventListener("touchend", onUp);
      window.addEventListener("touchcancel", onUp);
      window.addEventListener("blur", onUp);
    }

    ba.addEventListener("keydown", function (e) {
      var next = null;
      if (e.key === "ArrowLeft" || e.key === "Left") next = current - STEP;
      else if (e.key === "ArrowRight" || e.key === "Right") next = current + STEP;
      else if (e.key === "PageDown") next = current - BIG_STEP;
      else if (e.key === "PageUp") next = current + BIG_STEP;
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = 100;
      if (next === null) return;
      setSplit(next);
      e.preventDefault();
    });
  }

  window.BeforeAfter = { init: init };

  function autostart() {
    if (window.BeforeAfterConfig) init(window.BeforeAfterConfig);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", autostart);
  else autostart();
})();
